import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import Button from '~/components/atoms/button/button';
import Card from '~/components/atoms/card/card';
import BarsFilterIcon from '~/components/atoms/icon/barsFilter/BarsFilterIcon';
import ChevronIcon from '~/components/atoms/icon/chevron/ChevronIcon';
import { FilterVisibilityContext } from '~/components/main';
import CancelAllFiltersButton from '~/components/organisms/filters/cancelAllFiltersButton/CancelAllFiltersButton';
import Filter from '~/components/organisms/filters/Filter';
import { useRenderingInfo } from '~/contexts/useRenderingInfo';
import {
    Filter_ControlsState,
    Filters_ControlsState,
    Filters_State,
} from '~/core/features/filtering/types/filters.type';
import { ProductResults_State } from '~/core/state/types/state-results.schema';
import { CancelAllFilters, UpdateFilterControlsCurried } from '~/core/useSearchState';
import style from './productFilters.module.scss';
import { useProductsFiltersMessage } from './productFiltersMessage';

type MobileFiltersShowResultsButtonProps = {
    onClick: () => void;
    productsResults: number;
};
type CloseMobileFiltersButtonProps = {
    onClick: () => void;
};

const MobileFiltersShowResultsButton: React.FC<MobileFiltersShowResultsButtonProps> = ({
    onClick,
    productsResults,
}) => {
    const productsFiltersMessage = useProductsFiltersMessage(productsResults);

    return (
        <div className={style.showMobileFilterResults}>
            <Button onclick={onClick} className={style.filterCloseTrigger}>
                <span>
                    <p dangerouslySetInnerHTML={{ __html: productsFiltersMessage }}></p>
                </span>
            </Button>
        </div>
    );
};

const CloseMobileFiltersButton: React.FC<CloseMobileFiltersButtonProps> = ({ onClick }) => {
    return (
        <div className={style.closeMobileFilters}>
            <Button onclick={onClick} small={true} gray={true} block={true} className={style.closeMobileFiltersButton}>
                <FormattedMessage id="close_mobile_filters" defaultMessage="Zavřít filtry" />
                <span>
                    <ChevronIcon direction="down" width={3} />
                </span>
            </Button>
        </div>
    );
};

const OpenMobileFiltersButton: React.FC<{ onClick: () => void; hidden: boolean }> = ({ onClick, hidden }) => (
    <div
        className={classNames(
            style.serpFilterTriggerWrapper,
            hidden ? style.serpFilterTriggerWrapperHidden : style.serpFilterTriggerWrapperVisible
        )}
    >
        <Button onclick={onClick} className={style.serpFilterTrigger}>
            <BarsFilterIcon />
            <span>
                <p>
                    <FormattedMessage id="filter_submit" defaultMessage="Filtrovat" />
                </p>
            </span>
        </Button>
    </div>
);

export type ProductFiltersProps = {
    filtersState: Filters_State;
    filtersControls: Filters_ControlsState;
    updateFilterControls: UpdateFilterControlsCurried;
    querySetIdentifier: string;
    currency: string;
    cancelAllFilters: CancelAllFilters;
    productsResults: ProductResults_State;
    productResultsInView: boolean;
};

const ProductFilters = ({
    filtersState,
    filtersControls,
    currency,
    querySetIdentifier,
    updateFilterControls,
    cancelAllFilters,
    productsResults,
    productResultsInView,
}: ProductFiltersProps) => {
    const { isMobileVersion } = useRenderingInfo();
    const [isMobileFilterOpened, setIsMobileFilterOpened] = useState(false);
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const { isLayoutOverflowHidden, setIsLayoutOverflowHidden } = useContext(FilterVisibilityContext);

    const toggleMobileFilters = (): void => {
        if (isFilterVisible) {
            setIsFilterVisible(false);
        } else {
            setIsFilterVisible(true);
        }
        setIsLayoutOverflowHidden(!isLayoutOverflowHidden);
    };

    useEffect(() => {
        setIsMobileFilterOpened(isMobileVersion);
        setIsFilterVisible(!isMobileVersion);
    }, [isMobileVersion]);

    return (
        <>
            {isMobileVersion && isMobileFilterOpened ? (
                <OpenMobileFiltersButton onClick={toggleMobileFilters} hidden={!productResultsInView} />
            ) : null}
            <div
                className={classNames(
                    style.serpResultsCol1,
                    style.serpFilters,
                    isFilterVisible ? classNames(style.serpFiltersVisible) : classNames(style.serpFiltersHidden)
                )}
            >
                <div className={classNames(style.filterOverflow)}>
                    {isMobileVersion && isMobileFilterOpened && isFilterVisible ? (
                        <CloseMobileFiltersButton onClick={toggleMobileFilters} />
                    ) : null}
                    <div className={classNames(style.filterOverflowInside)}>
                        {filtersState.map((filterState, index) => {
                            const filterControls: Filter_ControlsState | undefined = filtersControls.find(
                                (filterControl) => filterControl.identifier === filterState.identifier
                            );

                            if (!filterControls) {
                                throw new Error(`Filter controls for filter ${filterState.identifier} not found`);
                            }

                            if (filterState.configuration.isSystem) {
                                return null;
                            }

                            return (
                                <Card
                                    secondary={true}
                                    ancestor={0 !== index}
                                    key={filterState.identifier}
                                    className={
                                        filterState.type === 'integer' || filterState.type === 'float'
                                            ? `${style.filtersCard} ${style.filtersCardSlider}`
                                            : style.filtersCard
                                    }
                                >
                                    <Filter
                                        querySetIdentifier={querySetIdentifier}
                                        filterState={filterState}
                                        filterControls={filterControls}
                                        currency={currency}
                                        updateFilterControls={updateFilterControls}
                                    />
                                </Card>
                            );
                        })}
                    </div>
                </div>
                <CancelAllFiltersButton onClick={() => cancelAllFilters({ querySetIdentifier: querySetIdentifier })} />
            </div>
            {isMobileVersion && isMobileFilterOpened && isFilterVisible ? (
                <MobileFiltersShowResultsButton
                    onClick={toggleMobileFilters}
                    productsResults={productsResults.totalRecords}
                />
            ) : null}
        </>
    );
};

export default ProductFilters;
